import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export const ProfitPage = () => {
  const { api } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [profitData, setProfitData] = useState(null);
  const [selectedBarIndex, setSelectedBarIndex] = useState(null);
  const [barDetailLoading, setBarDetailLoading] = useState(false);
  const [barDetail, setBarDetail] = useState(null);

  const periodLabels = {
    daily: 'du jour',
    weekly: 'de la semaine',
    monthly: 'du mois',
    yearly: 'de l’année'
  };

  const fetchProfitStats = async (selectedPeriod = period) => {
    setLoading(true);
    try {
      const response = await api.get(`/profit/stats?period=${selectedPeriod}`);
      setProfitData(response.data);
      setSelectedBarIndex(null);
      setBarDetail(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement du bénéfice');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitStats(period);
  }, [period]);

  const trend = profitData?.trend || [];
  let maxAbsProfit = 0;
  for (let index = 0; index < trend.length; index += 1) {
    const absValue = Math.abs(Number(trend[index].profit || 0));
    if (absValue > maxAbsProfit) {
      maxAbsProfit = absValue;
    }
  }

  let selectedPeriodProfit = 0;
  for (let index = 0; index < trend.length; index += 1) {
    selectedPeriodProfit += Number(trend[index].profit || 0);
  }

  const hasTrendData = trend.length > 0;
  const totalProfit = hasTrendData
    ? Number(selectedPeriodProfit.toFixed(2))
    : Number(profitData?.total_profit || 0);

  const handleSelectBar = async (index) => {
    setSelectedBarIndex(index);
    setBarDetailLoading(true);
    try {
      const response = await api.get(`/profit/bar-details?period=${period}&index=${index}`);
      setBarDetail(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement du détail bénéfice');
      setBarDetail(null);
    } finally {
      setBarDetailLoading(false);
    }
  };

  // Auto-select bar matching "today" when trend loads
  useEffect(() => {
    if (!trend || trend.length === 0 || selectedBarIndex !== null || barDetailLoading) {
      return;
    }
    const now = new Date();
    let idx;
    if (period === 'daily') {
      idx = now.getHours();
    } else if (period === 'weekly') {
      idx = (now.getDay() + 6) % 7;
    } else if (period === 'monthly') {
      idx = Math.floor((now.getDate() - 1) / 7);
    } else if (period === 'yearly') {
      idx = now.getMonth();
    } else {
      idx = trend.length - 1;
    }
    if (idx < 0 || idx >= trend.length) {
      idx = trend.length - 1;
    }
    handleSelectBar(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trend]);

  return (
    <div className="space-y-6" data-testid="profit-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="profit-page-title">Bénéfice</h1>
        <p className="text-slate-500 text-sm md:text-base" data-testid="profit-page-subtitle">CA - Charges, avec vue chronologique détaillée</p>
      </div>

      <Card className="border border-slate-200" data-testid="profit-total-card">
        <CardContent className="p-6">
          <p className="text-sm text-slate-500" data-testid="profit-total-label">Bénéfice total {periodLabels[period]}</p>
          <p
            className={`text-4xl font-bold mt-2 ${totalProfit >= 0 ? 'text-[#065F46]' : 'text-red-600'}`}
            data-testid="profit-total-value"
          >
            {totalProfit.toFixed(2)} €
          </p>
        </CardContent>
      </Card>

      <Card className="border border-slate-200" data-testid="profit-chart-card">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#065F46]" />
            <CardTitle className="font-outfit text-lg" data-testid="profit-chart-title">Graphique bénéfice</CardTitle>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-testid="profit-period-selector">
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'daily' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('daily')}
              data-testid="profit-period-daily"
            >
              Jour
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'weekly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('weekly')}
              data-testid="profit-period-weekly"
            >
              Semaine
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'monthly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('monthly')}
              data-testid="profit-period-monthly"
            >
              Mois
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-xs rounded transition-colors ${period === 'yearly' ? 'bg-[#065F46] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              onClick={() => setPeriod('yearly')}
              data-testid="profit-period-yearly"
            >
              Année
            </button>
          </div>
        </CardHeader>

        <CardContent className="p-4">
          {loading ? (
            <div className="py-12 flex items-center justify-center" data-testid="profit-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto" data-testid="profit-chart-wrapper">
                <div className="min-w-[700px] flex items-end gap-1" data-testid="profit-chart">
                  {trend.map((point, index) => {
                    const profit = Number(point.profit || 0);
                    const height = maxAbsProfit > 0 ? Math.max((Math.abs(profit) / maxAbsProfit) * 100, 6) : 0;
                    const barColor = profit >= 0 ? 'from-[#065F46] to-[#10B981]' : 'from-[#B91C1C] to-[#EF4444]';
                    const isActive = selectedBarIndex === index;

                    return (
                      <div key={`${point.label}-${index}`} className="flex-1 min-w-0 flex flex-col items-center gap-1" data-testid={`profit-chart-point-${index}`}>
                        <p className="text-[10px] text-slate-500" data-testid={`profit-chart-value-${index}`}>{profit.toFixed(0)}€</p>
                        <div className="h-24 w-full flex items-end justify-center">
                          <div
                            className={`w-5/6 rounded-t bg-gradient-to-t ${barColor} cursor-pointer ${isActive ? 'ring-2 ring-emerald-300' : ''}`}
                            style={{ height: `${height}%` }}
                            data-testid={`profit-chart-bar-${index}`}
                            title={`${point.label} • Bénéfice ${profit.toFixed(2)} €`}
                            onClick={() => handleSelectBar(index)}
                          />
                        </div>
                        <p className="text-[10px] text-slate-500" data-testid={`profit-chart-label-${index}`}>{point.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {barDetailLoading && (
                <div className="py-4 flex items-center justify-center" data-testid="profit-bar-detail-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-[#065F46]" />
                </div>
              )}

              {!barDetailLoading && barDetail && (
                <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3" data-testid="profit-bar-detail-card">
                  <div>
                    <p className="text-xs uppercase text-slate-500" data-testid="profit-bar-detail-title">Détail période {barDetail.label}</p>
                    <p className="text-xs text-slate-400" data-testid="profit-bar-detail-range">
                      {new Date(barDetail.start_date).toLocaleDateString('fr-FR')} - {new Date(barDetail.end_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-slate-50 rounded p-2" data-testid="profit-bar-detail-revenue-card">
                      <p className="text-xs text-slate-500">CA</p>
                      <p className="font-semibold text-slate-900" data-testid="profit-bar-detail-revenue-value">{Number(barDetail.total_revenue || 0).toFixed(2)} €</p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-testid="profit-bar-detail-charges-card">
                      <p className="text-xs text-slate-500">Charges</p>
                      <p className="font-semibold text-slate-900" data-testid="profit-bar-detail-charges-value">{Number(barDetail.total_charges || 0).toFixed(2)} €</p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-testid="profit-bar-detail-profit-card">
                      <p className="text-xs text-slate-500">Bénéfice</p>
                      <p className={`font-semibold ${Number(barDetail.total_profit || 0) >= 0 ? 'text-[#065F46]' : 'text-red-600'}`} data-testid="profit-bar-detail-profit-value">{Number(barDetail.total_profit || 0).toFixed(2)} €</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1" data-testid="profit-bar-detail-appointments-payments">
                      <p className="text-xs uppercase text-slate-500">Modes paiement RDV</p>
                      {(barDetail.appointment_payments || []).slice(0, 6).map((payment, index) => (
                        <div key={`${payment.method}-${index}`} className="text-xs flex items-center justify-between" data-testid={`profit-bar-detail-appointment-payment-row-${index}`}>
                          <span className="text-slate-700">{payment.method} ({payment.count})</span>
                          <span className="font-mono text-slate-900">{Number(payment.amount || 0).toFixed(2)} €</span>
                        </div>
                      ))}
                      {(barDetail.appointment_payments || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic" data-testid="profit-bar-detail-appointment-payments-empty">Aucun mode de paiement RDV</p>
                      )}
                    </div>

                    <div className="space-y-1" data-testid="profit-bar-detail-charge-payments">
                      <p className="text-xs uppercase text-slate-500">Modes paiement Charges</p>
                      {(barDetail.charge_payments || []).slice(0, 6).map((payment, index) => (
                        <div key={`${payment.method}-${index}`} className="text-xs flex items-center justify-between" data-testid={`profit-bar-detail-charge-payment-row-${index}`}>
                          <span className="text-slate-700">{payment.method} ({payment.count})</span>
                          <span className="font-mono text-slate-900">{Number(payment.amount || 0).toFixed(2)} €</span>
                        </div>
                      ))}
                      {(barDetail.charge_payments || []).length === 0 && (
                        <p className="text-xs text-slate-400 italic" data-testid="profit-bar-detail-charge-payments-empty">Aucun mode de paiement Charges</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1" data-testid="profit-bar-detail-providers">
                    <p className="text-xs uppercase text-slate-500">Prestataires</p>
                    {(barDetail.providers || []).slice(0, 6).map((provider, index) => (
                      <div key={`${provider.name}-${index}`} className="text-xs flex items-center justify-between" data-testid={`profit-bar-detail-provider-row-${index}`}>
                        <span className="text-slate-700">{provider.name} ({provider.count})</span>
                        <span className="font-mono text-slate-900">{Number(provider.amount || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.providers || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="profit-bar-detail-providers-empty">Aucun prestataire sur cette période</p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    <div data-testid="profit-bar-detail-appointments-list">
                      <p className="text-xs uppercase text-slate-500 mb-1">RDV</p>
                      {(barDetail.appointments || []).slice(0, 8).map((apt, index) => (
                        <div key={`${apt.id}-${index}`} className="text-xs flex items-center justify-between border-b border-slate-100 py-1" data-testid={`profit-bar-detail-appointment-row-${index}`}>
                          <span className="truncate text-slate-700">
                            {apt.client_name} • {apt.service_type}
                          </span>
                          <span className="font-mono text-slate-900">{Number(apt.price || 0).toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                    <div data-testid="profit-bar-detail-charges-list">
                      <p className="text-xs uppercase text-slate-500 mb-1">Charges</p>
                      {(barDetail.charges || []).slice(0, 8).map((charge, index) => (
                        <div key={`${charge.id}-${index}`} className="text-xs flex items-center justify-between border-b border-slate-100 py-1" data-testid={`profit-bar-detail-charge-row-${index}`}>
                          <span className="truncate text-slate-700">{charge.description}</span>
                          <span className="font-mono text-slate-900">{Number(charge.amount || 0).toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="profit-formula-card">
                <p className="text-sm text-slate-600 flex items-center gap-2" data-testid="profit-formula-text">
                  <TrendingUp className="w-4 h-4" />
                  Formule: Chiffre d’affaires - Charges
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
